import { useCallback, useRef } from 'react';
import type { ReactNode } from 'react';
import TimerIcon from '@repo/ui/src/components/Icons/TimerIcon';
import GrillIcon from '@repo/ui/src/components/Icons/GrillIcon';
import TempIcon from '@repo/ui/src/components/Icons/TempIcon';
import RestIcon from '@repo/ui/src/components/Icons/RestIcon';
import { classNames } from '../../utils/classNames';
import { formatDurationRange, formatTemp, getWeightLabel, resolveRestMinutes } from './_buildSchedule';
import { PlanActions } from './_PlanActions';
import { RecipeCard } from './_RecipeCard';
import { ScheduleTimeline } from './_ScheduleTimeline';
import type {
  CookTime,
  Method,
  PlanPanelLabels,
  ScheduleResult,
  ServingTier,
} from './types';

function escapeHtml(str: string): string {
  return str.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
}

function preparePanelClone(panel: HTMLElement) {
  const clone = panel.cloneNode(true) as HTMLElement;
  clone.querySelectorAll('.print\\:hidden').forEach((el) => el.remove());
  const dividers = clone.querySelectorAll('[class*="h-px"]');
  if (dividers.length > 0) {
    const lastDivider = dividers[dividers.length - 1];
    if (!lastDivider.nextElementSibling) lastDivider.remove();
  }
  return clone;
}

function openPrintTab(panel: HTMLElement, pageTitle?: string, buttonLabel?: string) {
  const clone = preparePanelClone(panel);
  clone.style.maxWidth = '700px';
  clone.style.margin = '0 auto';

  const styles = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'));
  const styleHTML = styles.map((s) => s.outerHTML).join('\n');

  const printTab = window.open('', '_blank');
  if (!printTab) return;

  printTab.document.write(`<!DOCTYPE html>
<html>
  <head>
    <title>${escapeHtml(pageTitle ?? 'Print Plan')}</title>
    ${styleHTML}
    <style>
      * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
      body {
        margin: 0;
        padding: 40px;
        background: white;
      }
      .print-header {
        display: flex;
        justify-content: flex-end;
        max-width: 700px;
        margin: 0 auto 24px;
      }
      .print-btn {
        padding: 8px 24px;
        background: #1a1a1a;
        color: white;
        border: none;
        cursor: pointer;
        font-family: inherit;
        font-size: 14px;
        text-transform: uppercase;
        letter-spacing: 1px;
      }
      @media print {
        .print-header { display: none !important; }
      }
    </style>
  </head>
  <body>
    <div class="print-header">
      <button class="print-btn" onclick="window.print()">${escapeHtml(buttonLabel ?? 'Print')}</button>
    </div>
    ${clone.outerHTML}
  </body>
</html>`);
  printTab.document.close();
}

interface PlanPanelProps {
  tier: ServingTier;
  method: Method;
  cookTime: CookTime | undefined;
  pullTempF: number;
  schedule: ScheduleResult;
  serveTimeMinutes: number;
  labels?: PlanPanelLabels;
  protein: string;
  unsupportedMessage?: string;
  theme: 'dark' | 'light';
}

interface StatProps {
  icon: ReactNode;
  label: string;
  value: string;
  theme: 'dark' | 'light';
}

function Stat({ icon, label, value, theme }: StatProps) {
  const isDark = theme === 'dark';
  return (
    <div>
      <div className={classNames('flex items-center gap-2 tra-text-web-body-l', isDark ? 'text-neutral-200 print:text-neutral-500' : 'text-neutral-500')}>
        {icon}
        {label}
      </div>
      <div className={classNames('mt-4 tra-text-web-heading-5', isDark ? 'text-neutral-100 print:text-neutral-900' : 'text-neutral-900')}>
        {value}
      </div>
    </div>
  );
}

export function PlanPanel({
  tier,
  method,
  cookTime,
  pullTempF,
  schedule,
  serveTimeMinutes,
  labels,
  protein,
  unsupportedMessage,
  theme,
}: PlanPanelProps) {
  const isDark = theme === 'dark';
  const panelRef = useRef<HTMLDivElement>(null);
  const handlePrint = useCallback(() => {
    if (panelRef.current) openPrintTab(panelRef.current, labels?.printPageTitle, labels?.printButtonLabel);
  }, [labels?.printPageTitle, labels?.printButtonLabel]);
  const effectivePullTemp = method.pullTempFOverride ?? pullTempF;
  const { min: restMin, max: restMax } = resolveRestMinutes(tier, cookTime);
  const weightLabel = getWeightLabel(tier);
  const showProtein = !tier.weight.displayLabel;

  return (
    <div
      ref={panelRef}
      className={classNames(
        'p-6',
        isDark ? 'bg-neutral-600 print:bg-neutral-100' : 'bg-neutral-100',
      )}
    >
      <div className="flex items-start justify-between">
        <div>
          <h3 className="mt-1 tra-text-web-heading-3">
            <span className="text-brand-300">{weightLabel}</span>
            {showProtein && (
              <span
                className={classNames(
                  'ml-2',
                  isDark ? 'text-white print:text-neutral-900' : 'text-neutral-900',
                )}
              >
                {protein}
              </span>
            )}
          </h3>
        </div>
      </div>

      {cookTime ? (
        <>
          <div className="mt-10">
            <div className="flex">
              <div className="flex-1">
                <Stat
                  icon={<TimerIcon className={classNames('w-6 h-6', isDark ? 'text-neutral-200' : 'text-neutral-900')} color="currentColor" showBackground={false} />}
                  label={labels?.cookTimeLabel ?? 'Cook Time'}
                  value={cookTime.cookTimeLabel ?? formatDurationRange(cookTime.cookMinutesMin, cookTime.cookMinutesMax, labels)}
                  theme={theme}
                />
              </div>
              <div className={classNames('mx-6 w-px opacity-15', isDark ? 'bg-neutral-100 print:bg-neutral-300' : 'bg-neutral-300')} />
              <div className="flex-1">
                <Stat
                  icon={<GrillIcon className={classNames('w-6 h-6', isDark ? 'text-neutral-200' : 'text-neutral-900')} color="currentColor" showBackground={false} />}
                  label={labels?.grillTempLabel ?? 'Grill Temp'}
                  value={formatTemp(method.tempF, method.tempFMax, labels)}
                  theme={theme}
                />
              </div>
            </div>
            <div className={classNames('my-6 h-px opacity-15', isDark ? 'bg-neutral-100 print:bg-neutral-300' : 'bg-neutral-300')} />
            <div className="flex">
              <div className="flex-1">
                <Stat
                  icon={<TempIcon className={classNames('w-6 h-6', isDark ? 'text-neutral-200' : 'text-neutral-900')} color="currentColor" showBackground={false} />}
                  label={labels?.pullTempLabel ?? 'Pull Temp'}
                  value={`${effectivePullTemp}\u00B0F`}
                  theme={theme}
                />
              </div>
              {restMin > 0 && (
                <>
                  <div className={classNames('mx-6 w-px opacity-15', isDark ? 'bg-neutral-100 print:bg-neutral-300' : 'bg-neutral-300')} />
                  <div className="flex-1">
                    <Stat
                      icon={<RestIcon className={classNames('w-6 h-6', isDark ? 'text-neutral-200' : 'text-neutral-900')} color="currentColor" showBackground={false} />}
                      label={labels?.restLabel ?? 'Rest'}
                      value={formatDurationRange(restMin, restMax, labels)}
                      theme={theme}
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          <div className={classNames('my-10 h-px opacity-15', isDark ? 'bg-neutral-100 print:bg-neutral-300' : 'bg-neutral-300')} />

          <div>
            <h4
              className={classNames(
                'tra-text-web-heading-4',
                isDark ? 'text-neutral-100 print:text-neutral-600' : 'text-neutral-600',
              )}
            >
              {labels?.scheduleTitle ?? 'Day-of schedule'}
            </h4>
            <ScheduleTimeline
              dayGroups={schedule.dayGroups}
              serveTimeMinutes={serveTimeMinutes}
              labels={labels}
              theme={theme}
            />
          </div>

          <PlanActions onPrint={handlePrint} printLabel={labels?.printLabel} shareLabel={labels?.shareLabel} linkCopiedLabel={labels?.linkCopiedLabel} theme={theme} />

          {method.recipe && (
            <RecipeCard
              recipe={method.recipe}
              title={labels?.recipeTitle ?? 'Suggested recipe'}
              theme={theme}
            />
          )}
        </>
      ) : (
        <p
          className={classNames(
            'mt-6 text-sm',
            isDark ? 'text-neutral-400' : 'text-neutral-500',
          )}
        >
          {unsupportedMessage ?? 'This combination is not available.'}
        </p>
      )}
    </div>
  );
}
